import { ref } from "vue";
import { useAuth } from "./useAuth";
import { useJobs } from "./useJobs";
import { readUserFacingError } from "../errorMessages";

/**
 * Composable for the retry-failed-job action. Wraps
 * POST /api/jobs/:id/retry plus the same auth/error-surface
 * patterns as :func:`useDeleteJob`.
 *
 * On 200: segment_api has flipped the row to status='processing' and
 * incremented retry_count. We don't mutate the local jobs cache here
 * — the JobDetailView polling cycle will pick up the new state via
 * GET /jobs/:id within one POLL_INTERVAL_MS.
 *
 * On 401: clear local auth (mirrors useDeleteJob).
 *
 * On 4xx/5xx: surface a user-facing message via
 * :func:`readUserFacingError` (USER_MESSAGES[code] for Problem Details,
 * generic-by-status otherwise — NEVER the raw response body).
 *
 * Module-level ``inFlight`` ref guards against double-submit (Retry
 * button optimistic-disables on click but the lock is cheap insurance).
 *
 * See ``docs/design/retry-failed-jobs-one-pager.md``.
 */

const inFlight = ref(false);

export type RetryResult =
  | { ok: true; retry_count: number }
  | { ok: false; error: string };

export function useRetryJob() {
  async function retryJob(jobId: string): Promise<RetryResult> {
    if (inFlight.value) {
      return { ok: false, error: "A retry is already in progress." };
    }
    inFlight.value = true;
    try {
      const resp = await fetch(`/api/jobs/${jobId}/retry`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: "{}",
      });
      if (resp.status === 200) {
        const body = (await resp.json()) as { retry_count: number };
        return { ok: true, retry_count: body.retry_count };
      }
      if (resp.status === 401) {
        const { signOut } = useAuth();
        await signOut();
        return { ok: false, error: "Session expired." };
      }
      if (resp.status === 404) {
        // Either someone else's job (existence leak guard returns 404)
        // OR a row that was deleted from another tab. Prune locally.
        const { jobs } = useJobs();
        jobs.value = jobs.value.filter((j) => j.job_id !== jobId);
        return { ok: false, error: "Job no longer exists." };
      }
      // 409 (cap exceeded or wrong status), 502 (trigger failure),
      // anything else — surface USER_MESSAGES[code]. Minimal migration
      // after readUserFacingError widening; rendering the Reference:
      // line in JobDetailView's retry-error toast is a follow-up.
      return { ok: false, error: (await readUserFacingError(resp)).message };
    } catch (err) {
      return {
        ok: false,
        error: `Cannot reach backend: ${(err as Error).message}`,
      };
    } finally {
      inFlight.value = false;
    }
  }

  return {
    retryJob,
    inFlight,
    /** Test-only. */
    _resetForTests() {
      inFlight.value = false;
    },
  };
}
